import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";

import ValidatedSelect from "../../components/ValidatedSelect/ValidatedSelect";
import ButtonFotter from "../../components/Button/ButtonFotter/ButtonFotter";
import ValidatedInput from "../../components/ValidatedInput/ValidatedInput";
import Typography from "../../components/Typography/Typography";
import Loader from "../../components/Loader/Loader";
import Button from "../../components/Button/Button";
import styles from "./ValidateIdentity.module.scss";
import Switch from "../../components/Switch/Switch";
import Modal from "../../components/Modal/Modal";
import codes from "../../utils/codes";

import { days, monthNumbers, years } from "../../utils/utils";
import { StateContext } from "../../contexts/state.context";
import { cedulaValidate } from "../../utils/validators";
import { formatStrNumber } from "../../utils/functions";
import { submitFormData } from "../../utils/services";
import { openExternalLink } from "../../utils/webview";

const RECAPTCHA_KEY = window.env.REACT_APP_GOOGLE_KEY;
const TREATMENT_POLICY = window.env.REACT_APP_TREATMENT_POLICY;
const TERMS_AND_CONDITIONS = window.env.REACT_APP_TERMS_AND_CONDITIONS;
const NUM_TRIES = window.env.REACT_APP_NUM_TRIES;

const ValidateIdentity = () => {
	const navigate = useNavigate();
	const { setFormData } = useContext(StateContext);

	const [policy, setPolicy] = useState(false);
	const [termsCondition, setTermsCondition] = useState(false);

	const [docType, setDocType] = useState(undefined);
	const [docTypeError, setDocTypeError] = useState(false);

	const [id, setId] = useState(undefined);
	const [idError, setIdError] = useState(false);

	const [day, setDay] = useState(undefined);
	const [year, setYear] = useState(undefined);
	const [month, setMonth] = useState(undefined);
	const [dateError, setDateError] = useState(false);

	const [loader, setLoader] = useState(false);
	const [wrongDataModal, setWrongDataModal] = useState(false);
	const [invalidDocumentModal, setInvalidDocumentModal] = useState(false);
	const [redirectModal, setRedirectModal] = useState(false);
	const [externalServiceModal, setExternalServiceModal] = useState(false);

	const [token, setToken] = useState(null);
	const [captchaError, setCaptchaError] = useState(false);

	const [numTries, setNumTries] = useState(NUM_TRIES);

	const [formResetKey, setFormResetKey] = useState(0);

	const resetFormFields = () => {
        setPolicy(false);
        setTermsCondition(false);
        setDocType(undefined);
        setDocTypeError(false);
        setId(undefined);
        setIdError(false);
        setDay(undefined);
        setMonth(undefined);
        setYear(undefined);
        setDateError(false);
        setToken(null);
        setCaptchaError(false);
        setWrongDataModal(false);
        setInvalidDocumentModal(false);
        setRedirectModal(false);
        setExternalServiceModal(false);
        setLoader(false);

        setFormData(null);

        setFormResetKey((k) => k + 1);
    };

	const handleNumTries = () => {
		setNumTries((n) => n - 1);
		setWrongDataModal();
	};

	const handleOpenPolicies = () => {
		openExternalLink(TREATMENT_POLICY, "validate_identity_policy");
	};

	const handleOpenTerms = () => {
		openExternalLink(TERMS_AND_CONDITIONS, "validate_identity_terms");
	};

	const validateExpDate = () => {
		if (
			!month ||
			!year ||
			!day ||
			month === "Mes" ||
			year === "Año" ||
			day === "Día"
		)
			return true;
		return false;
	};

	const getDocType = () => {
		switch (docType) {
			case "Cédula de ciudadanía":
				return "CC";
			case "Cédula de extranjería":
				return "CE";
			case "Pasaporte":
				return "PT";
			default:
				return "CC";
		}
	};

	const handleReturnClick = () => {
		navigate("/login");
	};

	const handleDisabledBtn = () => {
		if (
			policy &&
			termsCondition &&
			docType &&
			id &&
			day &&
			month &&
			year &&
			token
		)
			return false;
		else return true;
	};

	const handleContinueClick = () => {
		if (!docType || docType === "Selecciona tipo de documento") {
			setDocTypeError(true);
			return;
		}

		if (!id || cedulaValidate(id)) {
			setIdError(true);
			return;
		}

		if (validateExpDate()) {
			setDateError(true);
			return;
		}

		if (!token) {
			console.error("NO TOKEN");
			setCaptchaError(true);
			return;
		}

		setLoader(true);

		const rData = {};
		rData.id = id.replace(/[^\d]/g, "");
		rData.docType = getDocType();
		rData.fechaExpedicionDocumento = `${day}/${month}/${year}`;

		submitFormData({
			formInfo: rData,
		})
			.then(({ code, message }) => {
				switch (code) {
					case codes.JVB001.code:
						rData.apellidos = message.aniSource.apellidos;
						rData.nombres = message.aniSource.nombres;
						setFormData({ ...rData, captchaToken: token });
						navigate("/register/personal-data");
						break;
					case codes.JVB004.code:
						setLoader(false);
						setRedirectModal(true);
						break;
					case codes.JVB019.code:
						setLoader(false);
						setExternalServiceModal(true);
						break;

					default:
						setLoader(false);
						setWrongDataModal(true);
						break;
				}
			})
			.catch((e) => {
				console.log(e.data);
				setLoader(false);
			});
	};

	useEffect(() => {
		resetFormFields();
		// eslint-disable-next-line
	}, []);

	useEffect(() => {
		if (docType === "Cédula de extranjería" || docType === "Pasaporte")
			setInvalidDocumentModal(true);
	}, [docType]);

	return (
		<div className={styles.validateIdentityContainer}>
			<div className={styles.validateIdentityPolicyContainer}>
				<Switch
					key={`policy-${formResetKey}`}
					className={styles.validateIdentityPolicy}
					onChange={setPolicy}
				></Switch>
				<Typography>
					He leído y autorizo de manera previa, expresa e informada el
					tratamiento de mis datos personales conforme a la{" "}
					<span
						className={styles.validateIdentityPolicyBlueTxt}
						onClick={handleOpenPolicies}
					>
						Política de Tratamiento de Datos Personales
					</span>
					.
				</Typography>
			</div>

			<div className={styles.validateIdentityPolicyContainer}>
				<Switch
					key={`terms-${formResetKey}`}
					className={styles.validateIdentityPolicy}
					onChange={setTermsCondition}
				></Switch>
				<Typography>
					He leído y acepto los{" "}
					<span
						className={styles.validateIdentityPolicyBlueTxt}
						onClick={handleOpenTerms}
					>
						Términos y Condiciones
					</span>{" "}
					del programa Amigos Juan Valdez®.
				</Typography>
			</div>

			<ValidatedSelect
				key={`doctype-${formResetKey}`}
				className={styles.validateIdentitySelectContainer}
				label="Tipo de documento de identidad"
				placeholder="Selecciona tipo de documento"
				options={[
					"Cédula de ciudadanía",
					"Cédula de extranjería",
					"Pasaporte",
				]}
				onChange={setDocType}
				error={docTypeError}
				errorMessage="Campo inválido"
				setError={setDocTypeError}
			></ValidatedSelect>

			{docType && (
				<>
					<ValidatedInput
						key={`id-${formResetKey}`}
						className={styles.validateIdentityTextInputContainer}
						label="Número de documento de identidad"
						placeholder="X.XXX.XXX.XXX"
						type="text"
						inputMode="numeric"
						onChange={setId}
						error={idError}
						setError={setIdError}
						formatter={formatStrNumber}
					></ValidatedInput>

					<Typography
						className={styles.validateIdentityTextInputContainer}
						variant="h6"
						bold
					>
						Fecha de expedición del documento
					</Typography>

					<div className={styles.validateIdentityExpDateContainer}>
						<ValidatedSelect
							key={`day-${formResetKey}`}
							placeholder="Día"
							options={days}
							onChange={setDay}
							error={dateError}
							setError={setDateError}
							center
						></ValidatedSelect>
						<ValidatedSelect
							key={`month-${formResetKey}`}
							placeholder="Mes"
							options={monthNumbers}
							onChange={setMonth}
							error={dateError}
							setError={setDateError}
							center
						></ValidatedSelect>
						<ValidatedSelect
							key={`year-${formResetKey}`}
							placeholder="Año"
							options={years}
							onChange={setYear}
							error={dateError}
							setError={setDateError}
							center
						></ValidatedSelect>
					</div>

					<Typography
						className={styles.validateIdentityExpDateError}
						variant="p2"
						color={dateError ? "red" : "transparent"}
					>
						Fecha inválida
					</Typography>

					<div className={styles.validateIdentityReCaptchaContainer}>
						<div className={styles.validateIdentityReCaptcha}>
							<ReCAPTCHA
								key={`captcha-${formResetKey}`}
								sitekey={RECAPTCHA_KEY}
								hl="es"
								onChange={(token) => {
									setCaptchaError(false);
									setToken(token);
								}}
							></ReCAPTCHA>

							{captchaError && (
								<div
									className={
										styles.validateIdentityReCaptchaBorder
									}
								>
									<Typography
										className={
											styles.validateIdentityCaptchaError
										}
										variant="p2"
										color="red"
									>
										Debe realizar el reCAPTCHA
									</Typography>
								</div>
							)}
						</div>
					</div>
				</>
			)}

			<ButtonFotter
				onClick={handleContinueClick}
				disabled={handleDisabledBtn()}
			>
				Continuar
			</ButtonFotter>

			{wrongDataModal && (
				<Modal
					title="Los datos no coinciden."
					onClose={() => setWrongDataModal(false)}
				>
					<div>
						<Typography className={styles.modalText}>
							La información que ingresaste no coincide con un
							documento de identidad válido. Por favor, revisa los
							datos e inténtalo de nuevo.
						</Typography>

						<Button
							className={styles.modalBtn}
							onClick={handleNumTries}
						>
							Intentar de nuevo
						</Button>
					</div>
				</Modal>
			)}

			{invalidDocumentModal && (
				<Modal title="¡Ups! Documento no válido.">
					<div>
						<Typography className={styles.modalText}>
							Por el momento, solo puedes inscribirte con cédula
							de ciudadanía colombiana.<br></br>
							Si tienes otro tipo de documento, pronto estaremos
							habilitando esta opción.
						</Typography>

						<Button
							className={styles.modalBtn}
							onClick={() => navigate("/")}
						>
							Volver
						</Button>
					</div>
				</Modal>
			)}

			{redirectModal && (
				<Modal title="Ya estás registrado como Amigo Juan Valdez">
					<div>
						<Typography className={styles.modalText}>
							Por favor inicia sesión con los datos registrados.
							Si no recuerdas la contraseña, puedes recuperarla en{" "}
							<span className={styles.modalTextSpan}>
								"Olvide mi contraseña"
							</span>{" "}
							Dirígete a Soy Amigo Juan Valdez.
						</Typography>

						<Button
							className={styles.modalBtn}
							onClick={handleReturnClick}
						>
							Ir a Soy Amigo Juan Valdez
						</Button>
					</div>
				</Modal>
			)}

			{!numTries && (
				<Modal title="Protocolo de Seguridad Activado">
					<div>
						<Typography className={styles.modalText}>
							Tu solicitud de verificación no pudo ser procesada
							automáticamente y ha sido marcada para una revisión
							de seguridad. Para cualquier aclaración, contacta al
							área de soporte al correo <br></br>
							<span
								className={styles.validateIdentityPolicyBlueTxt}
							>
								cuentaconjuan@juanvaldezcafe.com
							</span>
						</Typography>

						<Button
							className={styles.modalBtn}
							onClick={() => navigate("/")}
						>
							Volver a Inicio
						</Button>
					</div>
				</Modal>
			)}

			{externalServiceModal && (
				<Modal
					title="Estamos presentando una intermitencia"
					onClose={() => setExternalServiceModal(false)}
				>
					<div>
						<Typography className={styles.modalText}>
							No pudimos validar tu información por una falla
                            temporal en servicios externos. Por favor intenta
                            nuevamente en unos minutos.
						</Typography>

						<Button
							className={styles.modalBtn}
							onClick={() => setExternalServiceModal(false)}
						>
							Cerrar
						</Button>
					</div>
				</Modal>
			)}

			{loader && <Loader></Loader>}
		</div>
	);
};

export default ValidateIdentity;
